import React, { useEffect, useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, FileText, Phone, MapPin, Package, Upload, ExternalLink, Users, Search } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const PART_STATUS = { joined: "Sudah Join", submitted: "Bukti Masuk", manager_approved: "Disetujui Manager", approved: "Selesai", rejected: "Ditolak", pending: "Menunggu" };

const STATUS = [
  { id: "processing", label: "Diproses", cls: "bg-showty-gold/20 text-showty-gold" },
  { id: "shipped", label: "Dikirim", cls: "bg-blue-100 text-blue-700" },
  { id: "delivered", label: "Diterima", cls: "bg-green-100 text-green-700" },
  { id: "cancelled", label: "Dibatalkan", cls: "bg-red-100 text-red-700" },
];
const statusOf = (id) => STATUS.find((s) => s.id === id) || STATUS[0];
const COURIERS = [
  { id: "SPX", label: "SPX (Shopee Express)" },
  { id: "ID", label: "iD Express" },
  { id: "JNE", label: "JNE" },
  { id: "JNT", label: "J&T Express" },
  { id: "SICEPAT", label: "SiCepat" },
  { id: "ANTERAJA", label: "AnterAja" },
];
const waLink = (wa) => `https://wa.me/${(wa || "").replace(/\D/g, "").replace(/^0/, "62")}`;

export default function EcommerceResiPanel() {
  const [list, setList] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [parts, setParts] = useState([]);
  const [partSearch, setPartSearch] = useState("");

  const load = useCallback(() => api.get("/ecommerce/resi").then((r) => setList(r.data)).catch(() => {}), []);
  const loadMembers = useCallback(() => api.get("/ecommerce/members").then((r) => setMembers(r.data)).catch(() => {}), []);
  const loadParts = useCallback(() => api.get("/ecommerce/campaign-participants").then((r) => setParts(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadMembers(); loadParts(); }, [load, loadMembers, loadParts]);

  const counts = {
    all: list.length,
    processing: list.filter((r) => r.status === "processing").length,
    shipped: list.filter((r) => r.status === "shipped").length,
    delivered: list.filter((r) => r.status === "delivered").length,
    cancelled: list.filter((r) => r.status === "cancelled").length,
  };
  const filtered = filter === "all" ? list : list.filter((r) => r.status === filter);
  const q = partSearch.trim().toLowerCase();
  const partsFiltered = !q ? parts : parts.filter((p) => (p.user_name || "").toLowerCase().includes(q) || (p.shopee_url || "").toLowerCase().includes(q) || (p.campaign_title || "").toLowerCase().includes(q));

  const setStatus = async (id, status) => {
    try { await api.patch(`/ecommerce/resi/${id}`, { status }); toast.success("Status diperbarui"); load(); }
    catch { toast.error("Gagal memperbarui status"); }
  };
  const remove = async (id) => { try { await api.delete(`/ecommerce/resi/${id}`); toast.success("Resi dihapus"); load(); } catch { toast.error("Gagal menghapus"); } };

  return (
    <div data-testid="ecommerce-resi-panel">
      <Tabs defaultValue="resi">
        <TabsList className="bg-showty-secondary mb-4">
          <TabsTrigger value="resi" data-testid="eco-tab-resi"><Package className="w-4 h-4 mr-1.5" /> Kelola Resi</TabsTrigger>
          <TabsTrigger value="peserta" data-testid="eco-tab-peserta"><Users className="w-4 h-4 mr-1.5" /> Peserta Campaign</TabsTrigger>
        </TabsList>
        <TabsContent value="resi">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="resi-summary">
        {[{ id: "all", label: "Total Resi" }, ...STATUS].map((s) => (
          <button key={s.id} onClick={() => setFilter(s.id)} data-testid={`resi-filter-${s.id}`}
            className={`rounded-2xl border p-4 text-left transition-colors ${filter === s.id ? "border-showty-red bg-showty-pink/20" : "border-border bg-white hover:bg-showty-pink/10"}`}>
            <p className="font-display text-3xl text-showty-navy">{counts[s.id]}</p>
            <p className="text-xs text-muted-foreground">{s.label || "Total Resi"}</p>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><Package className="w-5 h-5 text-showty-red" /> Daftar Resi Shopee</h3>
        <ResiDialog members={members} onDone={load} />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="resi-empty">
          <Package className="w-6 h-6 mx-auto mb-2 text-showty-red/50" /> Belum ada resi. Klik "Upload Resi" untuk menambahkan.
        </div>
      ) : (
        <div className="space-y-3" data-testid="resi-list">
          {filtered.map((r) => {
            const st = statusOf(r.status);
            return (
              <div key={r.id} className="rounded-xl border border-border bg-white p-4" data-testid={`resi-${r.id}`}>
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${st.cls}`} data-testid={`resi-status-badge-${r.id}`}>{st.label}</span>
                      <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-[#ee4d2d]/10 text-[#ee4d2d]">SHOPEE · {r.courier || "SPX"}</span>
                      {r.order_no && <span className="text-xs text-muted-foreground">Order: <span className="font-medium text-showty-navy">{r.order_no}</span></span>}
                    </div>
                    <p className="mt-1.5 font-medium text-showty-navy">{r.user_name} <span className="text-xs text-muted-foreground font-normal">· {r.user_email}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">No Resi: <span className="font-mono text-showty-navy">{r.tracking_number}</span>{r.shopee_url ? <span> · Shopee: <span className="text-[#ee4d2d] font-semibold">@{r.shopee_url}</span></span> : ""}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
                      {r.whatsapp ? <a href={waLink(r.whatsapp)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-green-700 hover:underline" data-testid={`resi-wa-${r.id}`}><Phone className="w-3 h-3" /> {r.whatsapp}</a> : <span className="text-muted-foreground italic">No WA belum diisi</span>}
                      {r.city && <span className="inline-flex items-center gap-1 text-showty-navy"><MapPin className="w-3 h-3 text-showty-red" /> {r.city}</span>}
                      <a href={`${API}/ecommerce/resi/${r.id}/file`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-showty-red hover:underline" data-testid={`resi-file-${r.id}`}><FileText className="w-3 h-3" /> Lihat File Resi</a>
                    </div>
                    {r.note && <p className="mt-1 text-xs text-muted-foreground italic">Catatan: {r.note}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                      <SelectTrigger className="h-9 w-36 text-xs" data-testid={`resi-set-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`resi-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </TabsContent>
        <TabsContent value="peserta">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><Users className="w-5 h-5 text-showty-red" /> Peserta Campaign</h3>
            <div className="relative w-full max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Cari nama / username Shopee..." value={partSearch} onChange={(e) => setPartSearch(e.target.value)} className="pl-9 rounded-full" data-testid="eco-part-search" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Data creator yang ikut campaign — cocokkan username Shopee dengan resi yang kamu upload.</p>
          <ParticipantsTable rows={partsFiltered} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ParticipantsTable = ({ rows }) => (
  rows.length === 0 ? (
    <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="eco-participants-empty">
      <Users className="w-6 h-6 mx-auto mb-2 text-showty-red/50" /> Belum ada creator yang ikut campaign.
    </div>
  ) : (
    <div className="overflow-x-auto rounded-2xl border border-border bg-white" data-testid="eco-participants-table">
      <table className="w-full text-sm">
        <thead className="bg-showty-cream/60 text-showty-navy">
          <tr>
            <th className="text-left px-4 py-3 font-semibold">Nama</th>
            <th className="text-left px-4 py-3 font-semibold">Username Shopee</th>
            <th className="text-left px-4 py-3 font-semibold">WhatsApp</th>
            <th className="text-left px-4 py-3 font-semibold">Kota</th>
            <th className="text-left px-4 py-3 font-semibold">Campaign</th>
            <th className="text-left px-4 py-3 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-border hover:bg-showty-pink/10" data-testid={`eco-part-${p.id}`}>
              <td className="px-4 py-3 text-showty-navy">{p.user_name}<div className="text-xs text-muted-foreground">{p.user_email}</div></td>
              <td className="px-4 py-3">{p.shopee_url ? <span className="inline-flex items-center rounded-full bg-[#ee4d2d]/10 text-[#ee4d2d] px-2 py-0.5 text-xs font-semibold" data-testid={`eco-part-shopee-${p.id}`}>@{p.shopee_url}</span> : <span className="text-muted-foreground italic text-xs">belum diisi</span>}</td>
              <td className="px-4 py-3">{p.whatsapp ? <a href={`https://wa.me/${p.whatsapp.replace(/\D/g, "").replace(/^0/, "62")}`} target="_blank" rel="noreferrer" className="text-green-700 hover:underline">{p.whatsapp}</a> : <span className="text-muted-foreground italic text-xs">—</span>}</td>
              <td className="px-4 py-3 text-showty-navy">{p.city || "—"}</td>
              <td className="px-4 py-3 text-showty-navy">{p.campaign_title}</td>
              <td className="px-4 py-3 text-showty-navy">{PART_STATUS[p.status] || p.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);

const ResiDialog = ({ members, onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ user_id: "", order_no: "", tracking_number: "", courier: "SPX", status: "processing", note: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.user_id) return toast.error("Pilih member penerima");
    if (!f.tracking_number.trim()) return toast.error("No Resi wajib diisi");
    if (!file) return toast.error("Upload file resi (PDF/foto)");
    const fd = new FormData();
    fd.append("user_id", f.user_id);
    fd.append("order_no", f.order_no);
    fd.append("tracking_number", f.tracking_number);
    fd.append("courier", f.courier);
    fd.append("status", f.status);
    fd.append("note", f.note);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/ecommerce/resi", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resi berhasil diupload");
      setOpen(false);
      setF({ user_id: "", order_no: "", tracking_number: "", courier: "SPX", status: "processing", note: "" });
      setFile(null);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload resi"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="resi-add-btn"><Plus className="w-4 h-4 mr-1" /> Upload Resi</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="resi-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-showty-navy">Upload Resi Shopee</DialogTitle>
          <DialogDescription>Pilih member penerima, isi data resi, dan unggah file resi (PDF atau foto).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Member Penerima</Label>
            <Select value={f.user_id} onValueChange={(v) => set("user_id", v)}>
              <SelectTrigger className="mt-1" data-testid="resi-member"><SelectValue placeholder="Pilih member terdaftar" /></SelectTrigger>
              <SelectContent>
                {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}{m.shopee_url ? ` · @${m.shopee_url}` : ""} — {m.role === "affiliator" ? "Affiliator" : "Creator"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>No Order (opsional)</Label><Input className="mt-1" value={f.order_no} onChange={(e) => set("order_no", e.target.value)} data-testid="resi-order-no" /></div>
            <div><Label>No Resi</Label><Input className="mt-1" value={f.tracking_number} onChange={(e) => set("tracking_number", e.target.value)} data-testid="resi-tracking-number" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kurir</Label>
              <Select value={f.courier} onValueChange={(v) => set("courier", v)}>
                <SelectTrigger className="mt-1" data-testid="resi-courier"><SelectValue /></SelectTrigger>
                <SelectContent>{COURIERS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1" data-testid="resi-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>File Resi (PDF / Foto)</Label>
            <label className="mt-1 flex items-center gap-2 rounded-lg border border-dashed border-border bg-showty-cream/40 px-4 py-3 text-sm cursor-pointer hover:bg-showty-pink/20" data-testid="resi-file-label">
              <Upload className="w-4 h-4 text-showty-red" />
              <span className="truncate">{file ? file.name : "Klik untuk pilih file resi..."}</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="resi-file-input" />
            </label>
          </div>
          <div><Label>Catatan (opsional)</Label><Textarea rows={2} className="mt-1" value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="resi-note" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="resi-save">{busy ? "Menyimpan..." : "Simpan Resi"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
